import { useEffect, useRef, useState } from "react";
import {
  Search,
  Mic,
  Plus,
  X,
  Filter,
  Sparkles,
  Download,
  SlidersHorizontal,
  Focus,
  Settings,
  CalendarRange,
  LogOut,
  Key,
} from "lucide-react";
import IagoSpinner from "../iago/IagoSpinner";
import clsx from "clsx";
import { BetaFeedbackModal } from "./BetaFeedbackModal";
import { useStore } from "../../stores/store";
import {
  priorityMeta,
  moodMeta,
  exportAsMarkdown,
  exportAsCSV,
  downloadFile,
} from "../../lib/utils";
import type { Mood, DeadlinePreset, SortKey, Priority } from "../../types";
import { supabase } from "../../lib/supabase";

interface Props {
  onRecord: () => void;
  onNewLane: () => void;
  onOpenDigest: () => void;
}

const DEADLINE_LABELS: Record<DeadlinePreset, string> = {
  today: "Hoje",
  this_week: "Esta semana",
  overdue: "Atrasados",
  no_date: "Sem prazo",
};

const SORT_LABELS: Record<SortKey, string> = {
  manual: "Manual",
  deadline: "Deadline",
  effort: "Complexidade",
  created: "Recentes",
};

const PRIORITIES: Priority[] = ["urgent", "high", "medium", "low"];
const MOODS: Mood[] = ["urgent", "exploratory", "routine", "creative"];

export function TopBar({ onRecord, onNewLane, onOpenDigest }: Props) {
  const searchQuery = useStore((s) => s.searchQuery);
  const setSearch = useStore((s) => s.setSearch);
  const priorityFilter = useStore((s) => s.priorityFilter);
  const setPriorityFilter = useStore((s) => s.setPriorityFilter);
  const categoryFilter = useStore((s) => s.categoryFilter);
  const setCategoryFilter = useStore((s) => s.setCategoryFilter);
  const categories = useStore((s) => s.categories);
  const moodFilter = useStore((s) => s.moodFilter);
  const setMoodFilter = useStore((s) => s.setMoodFilter);
  const deadlineFilter = useStore((s) => s.deadlineFilter);
  const setDeadlineFilter = useStore((s) => s.setDeadlineFilter);
  const sort = useStore((s) => s.sort);
  const setSort = useStore((s) => s.setSort);
  const clearFilters = useStore((s) => s.clearFilters);
  const cards = useStore((s) => s.cards);
  const lanes = useStore((s) => s.lanes);
  const health = useStore((s) => s.health);
  const toggleChat = useStore((s) => s.toggleChat);
  const chatOpen = useStore((s) => s.chatOpen);
  const focusLaneId = useStore((s) => s.focusLaneId);
  const setFocusLane = useStore((s) => s.setFocusLane);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);
  const userEmail = useStore((s) => s.userEmail);
  const userSettings = useStore((s) => s.userSettings);
  const setOnboardingOpen = useStore((s) => s.setOnboardingOpen);

  const activeProvider = health?.providers?.groq
    ? "groq"
    : health?.providers?.openrouter
      ? "openrouter"
      : health?.providers?.cerebras
        ? "cerebras"
        : null;

  // Conta apenas filtros de seleção (sort fica separado)
  const activeFilterCount =
    (priorityFilter ? 1 : 0) +
    (categoryFilter != null ? 1 : 0) +
    (moodFilter ? 1 : 0) +
    (deadlineFilter ? 1 : 0);

  const [betaOpen, setBetaOpen] = useState(false);

  function doExport(fmt: "md" | "json" | "csv") {
    const date = new Date().toISOString().slice(0, 10);
    if (fmt === "md") {
      downloadFile(
        `onboard-${date}.md`,
        exportAsMarkdown(lanes, cards, categories),
        "text/markdown",
      );
    } else if (fmt === "json") {
      downloadFile(
        `onboard-${date}.json`,
        JSON.stringify(
          { exportedAt: new Date().toISOString(), lanes, cards, categories },
          null,
          2,
        ),
        "application/json",
      );
    } else {
      downloadFile(
        `onboard-${date}.csv`,
        exportAsCSV(cards, categories),
        "text/csv",
      );
    }
  }

  return (
    <div className="glass sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-white/5 px-3 py-2 md:flex-nowrap md:gap-3 md:px-5 md:py-3">
      {/* Marca */}
      <div className="group flex items-center gap-2.5">
        <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 shadow-[0_0_24px_rgba(168,85,247,0.45)] transition group-hover:shadow-[0_0_32px_rgba(168,85,247,0.65)]">
          <div className="absolute inset-[1.5px] rounded-[10px] bg-bg-0/70 backdrop-blur-md" />
          <svg
            viewBox="0 0 24 24"
            className="relative h-4 w-4 text-white"
            fill="currentColor"
          >
            <rect x="3" y="4" width="4" height="16" rx="1" />
            <rect x="10" y="4" width="4" height="11" rx="1" />
            <rect x="17" y="4" width="4" height="7" rx="1" />
          </svg>
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-bg-0 pulse-dot" />
        </div>
        <div className="hidden flex-col leading-tight sm:flex">
          <h1 className="font-mono text-sm font-semibold tracking-tight brand-text">
            on_board
          </h1>
        </div>
      </div>

      <div className="mx-2 hidden h-7 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent sm:block" />

      {/* Quebra de linha em mobile (force layout 2 linhas) */}
      <div className="order-3 basis-full md:hidden" />

      {/* Busca */}
      <div className="relative order-4 flex-1 md:order-2 md:max-w-sm">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500 transition group-focus-within:text-violet-300" />
        <input
          value={searchQuery}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar"
          className="w-full rounded-lg border border-white/10 bg-black/30 py-1.5 pl-9 pr-14 text-sm text-slate-100 shadow-inner shadow-black/20 placeholder:text-slate-500 transition focus:border-violet-500/60 focus:bg-black/40 focus:outline-none focus:shadow-[0_0_0_3px_rgba(168,85,247,0.12)]"
        />
        {searchQuery ? (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-white/5 hover:text-slate-100"
            aria-label="Limpar busca"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded bg-white/5 px-1.5 py-0.5 font-mono text-[9px] text-slate-500 ring-1 ring-white/5 md:block">
            ⌘K
          </kbd>
        )}
      </div>

      {/* Filtros consolidados */}
      <div className="order-5 md:order-3">
      <FiltersPopover
        activeCount={activeFilterCount}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        moodFilter={moodFilter}
        setMoodFilter={setMoodFilter}
        deadlineFilter={deadlineFilter}
        setDeadlineFilter={setDeadlineFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        categories={categories}
        clearAll={clearFilters}
        openCategorySettings={() => setSettingsOpen(true)}
      />
      </div>

      {/* Ordenação */}
      <div className="order-6 md:order-4">
        <SortPopover sort={sort} setSort={setSort} />
      </div>

      {/* Direita */}
      <div className="order-2 ml-auto flex items-center gap-2 md:order-5">
        {focusLaneId !== null && (
          <button
            onClick={() => setFocusLane(null)}
            className="flex items-center gap-1 rounded-lg bg-violet-500/20 px-2 py-1.5 text-xs text-violet-200"
          >
            <Focus className="h-3.5 w-3.5" /> Sair do foco
          </button>
        )}

        {activeProvider && (
          <div
            className="hidden items-center gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] text-emerald-300 lg:flex"
            title={`Provedor IA ativo: ${activeProvider}`}
          >
            <span className="pulse-dot inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            AI · {activeProvider}
          </div>
        )}

        {/* Espaço BETA — feedback de beta tester */}
        <button
          onClick={() => setBetaOpen(true)}
          className="group relative flex items-center gap-1.5 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-fuchsia-200 transition hover:border-fuchsia-300/50 hover:bg-fuchsia-500/15"
          title="Mandar feedback, bug ou ideia"
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Espaço BETA</span>
        </button>

        {/* Menu "Mais": ações secundárias */}
        <MoreMenu
          onExport={doExport}
          onDigest={onOpenDigest}
          onNewLane={onNewLane}
          onSettings={() => setSettingsOpen(true)}
          onRedoTutorial={() => useStore.getState().setTourOpen(true)}
        />

        {/* Ação primária — Nova tarefa */}
        <button
          data-tour="record"
          onClick={onRecord}
          disabled={!userSettings.hasGroqKey}
          title={
            userSettings.hasGroqKey
              ? "Nova tarefa por voz (⌘N)"
              : "Configure sua chave Groq primeiro"
          }
          className="group relative flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 px-3.5 py-1.5 text-xs font-semibold text-white shadow-[0_0_24px_rgba(168,85,247,0.45)] transition hover:-translate-y-px hover:shadow-[0_0_32px_rgba(168,85,247,0.65)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-200 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0"
        >
          <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          <Mic className="relative h-3.5 w-3.5" />
          <span className="relative hidden sm:inline">Nova tarefa</span>
          <span className="relative ml-1 hidden rounded bg-white/15 px-1 py-0.5 font-mono text-[9px] xl:inline">
            ⌘N
          </span>
        </button>

        {/* Avatar */}
        <UserMenu
          userEmail={userEmail}
          hasGroqKey={userSettings.hasGroqKey}
          groqLast4={userSettings.groqLast4}
          openOnboarding={() => setOnboardingOpen(true)}
          openSettings={() => setSettingsOpen(true)}
        />
      </div>

      <BetaFeedbackModal open={betaOpen} onClose={() => setBetaOpen(false)} />
    </div>
  );
}

/** Hook utilitário para fechar popover ao clicar fora ou pressionar Esc. */
function usePopover(onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return ref;
}

interface FiltersPopoverProps {
  activeCount: number;
  priorityFilter: string | null;
  setPriorityFilter: (p: string | null) => void;
  moodFilter: Mood | null;
  setMoodFilter: (m: Mood | null) => void;
  deadlineFilter: DeadlinePreset | null;
  setDeadlineFilter: (d: DeadlinePreset | null) => void;
  categoryFilter: number | null;
  setCategoryFilter: (id: number | null) => void;
  categories: { id: number; name: string; color: string }[];
  clearAll: () => void;
  openCategorySettings: () => void;
}

function FiltersPopover(p: FiltersPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(() => setOpen(false));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0",
          p.activeCount > 0 || open
            ? "border-violet-500/40 bg-violet-500/10 text-violet-200"
            : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20 hover:text-slate-200",
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Filter className="h-3.5 w-3.5" />
        Filtros
        {p.activeCount > 0 && (
          <span className="ml-0.5 rounded-full bg-violet-500/30 px-1.5 py-px text-[10px] font-semibold text-violet-100">
            {p.activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="glass absolute left-0 top-9 z-30 w-72 overflow-hidden rounded-xl p-3 shadow-elevated">
          <FilterSection title="Prioridade">
            <div className="flex flex-wrap gap-1">
              <Chip
                active={!p.priorityFilter}
                onClick={() => p.setPriorityFilter(null)}
              >
                Todas
              </Chip>
              {PRIORITIES.map((pr) => (
                <Chip
                  key={pr}
                  active={p.priorityFilter === pr}
                  onClick={() => p.setPriorityFilter(pr)}
                  className={priorityMeta[pr].color}
                >
                  {priorityMeta[pr].label}
                </Chip>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Mood">
            <div className="flex flex-wrap gap-1">
              <Chip
                active={!p.moodFilter}
                onClick={() => p.setMoodFilter(null)}
              >
                Todos
              </Chip>
              {MOODS.map((m) => (
                <Chip
                  key={m}
                  active={p.moodFilter === m}
                  onClick={() => p.setMoodFilter(m)}
                >
                  <span
                    className="mr-1 inline-block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: moodMeta[m].border }}
                  />
                  {moodMeta[m].label}
                </Chip>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Prazo">
            <div className="flex flex-wrap gap-1">
              <Chip
                active={!p.deadlineFilter}
                onClick={() => p.setDeadlineFilter(null)}
              >
                Qualquer
              </Chip>
              {(Object.keys(DEADLINE_LABELS) as DeadlinePreset[]).map((d) => (
                <Chip
                  key={d}
                  active={p.deadlineFilter === d}
                  onClick={() => p.setDeadlineFilter(d)}
                >
                  {DEADLINE_LABELS[d]}
                </Chip>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Categoria">
            <div className="flex flex-wrap gap-1">
              <Chip
                active={p.categoryFilter == null}
                onClick={() => p.setCategoryFilter(null)}
              >
                Todas
              </Chip>
              {p.categories.length === 0 ? (
                <span className="px-2 py-1 text-[10px] text-slate-500">
                  Sem categorias ainda.
                </span>
              ) : (
                p.categories.map((c) => (
                  <Chip
                    key={c.id}
                    active={p.categoryFilter === c.id}
                    onClick={() => p.setCategoryFilter(c.id)}
                  >
                    <span
                      className="mr-1 inline-block h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                    {c.name}
                  </Chip>
                ))
              )}
            </div>
            <button
              onClick={() => {
                p.openCategorySettings();
                setOpen(false);
              }}
              className="mt-1.5 text-[10px] text-violet-300 hover:text-violet-200"
            >
              Gerenciar categorias…
            </button>
          </FilterSection>

          {p.activeCount > 0 && (
            <div className="mt-2 flex items-center justify-between border-t border-white/5 pt-2">
              <span className="text-[10px] text-slate-400">
                {p.activeCount} filtro{p.activeCount > 1 ? "s" : ""} ativo
                {p.activeCount > 1 ? "s" : ""}
              </span>
              <button
                onClick={() => {
                  p.clearAll();
                  setOpen(false);
                }}
                className="rounded-md bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200 hover:bg-rose-500/20"
              >
                Limpar tudo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-slate-500">
        {title}
      </div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded-md border px-2 py-0.5 text-[11px] transition",
        active
          ? "border-violet-400/50 bg-violet-500/15 text-violet-100"
          : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100",
        className,
      )}
    >
      {children}
    </button>
  );
}

function SortPopover({
  sort,
  setSort,
}: {
  sort: SortKey;
  setSort: (s: SortKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(() => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0",
          sort !== "manual" || open
            ? "border-violet-500/40 bg-violet-500/10 text-violet-200"
            : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20 hover:text-slate-200",
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Ordem:</span> {SORT_LABELS[sort]}
      </button>
      {open && (
        <div className="glass absolute left-0 top-9 z-30 w-44 overflow-hidden rounded-xl p-1 shadow-elevated">
          {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
            <button
              key={k}
              onClick={() => {
                setSort(k);
                setOpen(false);
              }}
              className={clsx(
                "block w-full rounded-md px-2 py-1.5 text-left text-xs transition hover:bg-white/5",
                k === sort ? "text-violet-300" : "text-slate-200",
              )}
            >
              {SORT_LABELS[k]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreMenu({
  onExport,
  onDigest,
  onNewLane,
  onSettings,
  onRedoTutorial,
}: {
  onExport: (fmt: "md" | "json" | "csv") => void;
  onDigest: () => void;
  onNewLane: () => void;
  onSettings: () => void;
  onRedoTutorial: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [exportSub, setExportSub] = useState(false);
  const ref = usePopover(() => {
    setOpen(false);
    setExportSub(false);
  });

  return (
    <div ref={ref} className="relative" data-tour="settings">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.02] p-1.5 text-slate-300 transition hover:border-white/20 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0"
        title="Mais ações"
        aria-label="Mais ações"
      >
        <Settings className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-30 w-56 overflow-hidden rounded-xl border border-white/10 bg-bg-2/[0.97] p-1 text-sm shadow-elevated backdrop-blur-md">
          <div className="relative">
            <MenuButton
              onClick={() => setExportSub((v) => !v)}
              icon={<Download className="h-3.5 w-3.5" />}
              chevron
            >
              Exportar
            </MenuButton>
            {exportSub && (
              <div className="ml-6 mt-0.5 rounded-md border border-white/5 bg-white/[0.02] p-1">
                <MenuButton
                  onClick={() => {
                    onExport("md");
                    setOpen(false);
                  }}
                >
                  Markdown
                </MenuButton>
                <MenuButton
                  onClick={() => {
                    onExport("json");
                    setOpen(false);
                  }}
                >
                  JSON
                </MenuButton>
                <MenuButton
                  onClick={() => {
                    onExport("csv");
                    setOpen(false);
                  }}
                >
                  CSV
                </MenuButton>
              </div>
            )}
          </div>

          <MenuButton
            onClick={() => {
              setOpen(false);
              onDigest();
            }}
            icon={<CalendarRange className="h-3.5 w-3.5" />}
          >
            Resumo da semana
          </MenuButton>

          <div className="my-1 border-t border-white/5" />

          <MenuButton
            onClick={() => {
              setOpen(false);
              onSettings();
            }}
            icon={<Settings className="h-3.5 w-3.5" />}
          >
            Configurações
          </MenuButton>

          <MenuButton
            onClick={() => {
              setOpen(false);
              onRedoTutorial();
            }}
            icon={<Sparkles className="h-3.5 w-3.5 text-fuchsia-300" />}
          >
            Refazer tutorial
          </MenuButton>

          <MenuButton
            onClick={() => {
              setOpen(false);
              onNewLane();
            }}
            icon={<Plus className="h-3.5 w-3.5" />}
          >
            Nova raia
          </MenuButton>
        </div>
      )}
    </div>
  );
}

function MenuButton({
  onClick,
  icon,
  children,
  disabled,
  chevron,
}: {
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  chevron?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-slate-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon && <span className="flex h-4 w-4 items-center justify-center">{icon}</span>}
      <span className="flex-1">{children}</span>
      {chevron && <span className="text-slate-500">›</span>}
    </button>
  );
}

function UserMenu({
  userEmail,
  hasGroqKey,
  groqLast4,
  openOnboarding,
  openSettings,
}: {
  userEmail: string | null;
  hasGroqKey: boolean;
  groqLast4: string | null;
  openOnboarding: () => void;
  openSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(() => setOpen(false));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="group relative flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-800 ring-1 ring-white/10 transition hover:ring-violet-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"
        title={userEmail || "Conta"}
        aria-label="Menu da conta"
      >
        <span className="font-mono text-[11px] font-semibold text-slate-200">
          {(userEmail?.[0] || "?").toUpperCase()}
        </span>
        <span
          className={clsx(
            "absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-bg-0",
            hasGroqKey ? "bg-emerald-400 pulse-dot" : "bg-amber-400",
          )}
          title={hasGroqKey ? "Chave Groq configurada" : "Chave Groq pendente"}
        />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-30 w-60 overflow-hidden rounded-xl border border-white/10 bg-bg-2/[0.97] p-1 text-sm shadow-elevated backdrop-blur-md">
          <div className="border-b border-white/5 px-3 py-2 text-[11px] text-slate-400 break-all">
            {userEmail}
          </div>
          <MenuButton
            onClick={() => {
              setOpen(false);
              openOnboarding();
            }}
            icon={<Key className="h-3.5 w-3.5 text-violet-300" />}
          >
            {hasGroqKey ? `Chave Groq (···${groqLast4})` : "Configurar chave Groq"}
            {!hasGroqKey && (
              <span className="ml-auto text-[10px] text-amber-300">!</span>
            )}
          </MenuButton>
          <MenuButton
            onClick={() => {
              setOpen(false);
              openSettings();
            }}
            icon={<Settings className="h-3.5 w-3.5" />}
          >
            Configurações
          </MenuButton>
          <div className="my-1 border-t border-white/5" />
          <button
            onClick={async () => {
              setOpen(false);
              await supabase.auth.signOut();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-rose-300 hover:bg-rose-500/10"
          >
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}
